import type { ReactElement } from "react";
import { routeUrl } from "@/app/routes";
import { AbsenteesReadCard } from "@/features/copilot/components/read-cards/absentees-read-card";
import { GoalsReadCard } from "@/features/copilot/components/read-cards/goals-read-card";
import { LeaveBalancesReadCard } from "@/features/copilot/components/read-cards/leave-balances-read-card";
import { PendingSurveysReadCard } from "@/features/copilot/components/read-cards/pending-surveys-read-card";
import { PeopleReadCard } from "@/features/copilot/components/read-cards/people-read-card";
import { PulseActionsReadCard } from "@/features/copilot/components/read-cards/pulse-actions-read-card";
import { PulseInsightsReadCard } from "@/features/copilot/components/read-cards/pulse-insights-read-card";
import { PulseScoresReadCard } from "@/features/copilot/components/read-cards/pulse-scores-read-card";
import {
  attendanceSetupTarget,
  leaveSetupTarget,
  type SetupStatusCardTarget,
  SetupStatusReadCard,
} from "@/features/copilot/components/read-cards/setup-status-read-card";
import { TodosReadCard } from "@/features/copilot/components/read-cards/todos-read-card";
import type { CopilotReadPayload } from "@/features/copilot/lib/copilot-read-payload";
import {
  absenteesDataSchema,
  goalsDataSchema,
  leaveBalancesDataSchema,
  pendingSurveysDataSchema,
  peopleDataSchema,
  pulseActionsDataSchema,
  pulseInsightsDataSchema,
  pulseScoresDataSchema,
  setupStatusDataSchema,
  todosDataSchema,
} from "@/features/copilot/schemas/copilot-read-cards.schema";

/// No page shows a person's own per-dimension scores (My pulse is the survey list), so that card
/// has no "View all" rather than a link to something else.
const SCORE_SCOPES: Record<string, { title: string; href?: (slug: string) => string }> = {
  "pulse.scores.mine.get": { title: "Your pulse" },
  "pulse.scores.team.get": { title: "Team pulse", href: routeUrl.pulse.insights },
  "pulse.scores.workspace.get": { title: "Workspace pulse", href: routeUrl.pulse.insights },
};

const SETUP_CARDS: Record<
  string,
  { title: string; target: (slug: string) => SetupStatusCardTarget }
> = {
  "leave.setupStatus.get": { title: "Leave setup", target: leaveSetupTarget },
  "attendance.setupStatus.get": { title: "Attendance setup", target: attendanceSetupTarget },
};

function peopleTitle(capability: string, input: unknown): string | null {
  if (capability === "people.list") return "People";
  if (capability !== "people.reports.list") return null;
  // With personId the model listed someone else's reports, not the caller's.
  const forSomeoneElse =
    typeof input === "object" &&
    input !== null &&
    typeof Reflect.get(input, "personId") === "string";
  return forSomeoneElse ? "Reports" : "Your reports";
}

/// The card for a finished read, or null when the row should stay a plain line: a capability
/// without a card, a shape that does not validate, or nothing to show. A plain function, not a
/// component, so the caller can choose between card and line before rendering either.
export function renderCopilotReadCard(
  {
    capability,
    data,
    input,
    truncated,
  }: CopilotReadPayload & { truncated?: Record<string, number> },
  slug: string,
): ReactElement | null {
  // Rows the server cut off are not counted in nextCursor, so a cut also means more exists.
  const cut = truncated !== undefined;
  const scope = SCORE_SCOPES[capability];
  if (scope) {
    const parsed = pulseScoresDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.cells.length === 0) return null;
    return (
      <PulseScoresReadCard
        title={scope.title}
        cells={parsed.data.cells}
        viewAllHref={scope.href?.(slug)}
      />
    );
  }
  const setup = SETUP_CARDS[capability];
  if (setup) {
    const parsed = setupStatusDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.steps.length === 0) return null;
    return (
      <SetupStatusReadCard
        title={setup.title}
        steps={parsed.data.steps}
        target={setup.target(slug)}
      />
    );
  }
  const title = peopleTitle(capability, input);
  if (title) {
    const parsed = peopleDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <PeopleReadCard
        title={title}
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "pulse.insights.list") {
    const parsed = pulseInsightsDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <PulseInsightsReadCard
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "pulse.actions.list") {
    const parsed = pulseActionsDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <PulseActionsReadCard
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "todos.list") {
    const parsed = todosDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <TodosReadCard
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "goals.list") {
    const parsed = goalsDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <GoalsReadCard
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "leave.absentees.list") {
    const parsed = absenteesDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <AbsenteesReadCard
        items={parsed.data.items}
        date={parsed.data.date}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "pulse.surveys.pending.list") {
    const parsed = pendingSurveysDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.items.length === 0) return null;
    return (
      <PendingSurveysReadCard
        items={parsed.data.items}
        slug={slug}
        hasMore={parsed.data.nextCursor !== undefined || cut}
      />
    );
  }
  if (capability === "leave.balances.list") {
    const parsed = leaveBalancesDataSchema.safeParse(data);
    if (!parsed.success || parsed.data.balances.length === 0) return null;
    return <LeaveBalancesReadCard balances={parsed.data.balances} slug={slug} />;
  }
  return null;
}
